import React, { useState, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  Target,
  BarChart3,
  PieChart,
  Calendar,
  Download,
  RefreshCw,
  ArrowUp,
  ArrowDown,
  Minus,
  BookOpen,
  Award,
  AlertCircle,
  Database,
  Shield
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  getDoc,
  limit,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';

/** JSON-safe export for Firestore field values */
const serializeFirestoreValue = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'object') return value;
  if (typeof value.toDate === 'function') {
    try {
      return { _firestoreType: 'Timestamp', iso: value.toDate().toISOString() };
    } catch {
      return null;
    }
  }
  if (Array.isArray(value)) return value.map(serializeFirestoreValue);
  if (
    typeof value.latitude === 'number' &&
    typeof value.longitude === 'number'
  ) {
    return { _firestoreType: 'GeoPoint', latitude: value.latitude, longitude: value.longitude };
  }
  const out = {};
  for (const k of Object.keys(value)) {
    const v = serializeFirestoreValue(value[k]);
    if (v !== undefined) out[k] = v;
  }
  return out;
};

const docToExportRow = (d) => ({ id: d.id, ...serializeFirestoreValue(d.data()) });

async function fetchByCoachingId(collectionName, coachingId) {
  const q = query(collection(db, collectionName), where('coachingId', '==', coachingId));
  const snap = await getDocs(q);
  return snap.docs.map(docToExportRow);
}

/** Firestore "in" queries are limited to 10 values per query */
async function fetchByBatchIdIn(collectionName, batchIds) {
  if (!batchIds.length) return [];
  const out = [];
  const seen = new Set();
  for (let i = 0; i < batchIds.length; i += 10) {
    const chunk = batchIds.slice(i, i + 10);
    const q = query(collection(db, collectionName), where('batchId', 'in', chunk));
    const snap = await getDocs(q);
    snap.docs.forEach((d) => {
      if (!seen.has(d.id)) {
        seen.add(d.id);
        out.push(docToExportRow(d));
      }
    });
  }
  return out;
}

async function fetchStaffUsers(coachingId) {
  const q = query(collection(db, 'users'), where('coachingId', '==', coachingId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const row = docToExportRow(d);
    delete row.fcmToken;
    return row;
  });
}

const BusinessIntelligenceDashboard = ({ coachingId, timeRange = '30' }) => {
  // Debug user permissions
  React.useEffect(() => {
    const checkUserPermissions = async () => {
      try {
        const auth = window.firebase?.auth;
        if (auth?.currentUser) {
          const user = auth.currentUser;
          console.log('Current user ID:', user.uid);
          console.log('Current user email:', user.email);
          
          // Try to get user profile from Firestore
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            console.log('User profile data:', userData);
            console.log('User role:', userData.role);
            console.log('User coachingId:', userData.coachingId);
            console.log('Is coaching admin?', userData.role === 'coaching_admin');
            console.log('Requested coaching ID:', coachingId);
            console.log('Coaching ID matches?', userData.coachingId === coachingId);
          } else {
            console.log('User profile not found in Firestore');
          }
        } else {
          console.log('No authenticated user');
        }
      } catch (error) {
        console.log('Error checking user permissions:', error);
      }
    };
    
    checkUserPermissions();
  }, []);
  
  // Centralized data state
  const [rawData, setRawData] = useState({
    batches: [],
    coaches: [],
    students: [],
    enrollments: [],
    payments: []
  });
  
  const [dashboardData, setDashboardData] = useState({
    totalRevenue: 0,
    totalStudents: 0,
    activeCoaches: 0,
    activeBatches: 0,
    revenueGrowth: 0,
    studentGrowth: 0,
    averageRevenuePerStudent: 0,
    retentionRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRange);
  const [revenueByCoach, setRevenueByCoach] = useState([]);
  const [studentsByBatch, setStudentsByBatch] = useState([]);
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [topPerformingBatches, setTopPerformingBatches] = useState([]);
  const [error, setError] = useState(null);
  const [dataRetentionEnabled, setDataRetentionEnabled] = useState(false);
  const [loadingRetention, setLoadingRetention] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Step 1: Load all raw data with PARALLEL queries for better performance
  const loadRawData = useCallback(async () => {
    if (!coachingId) return null;
    
    try {
      console.log('🚀 Loading raw data for coaching ID:', coachingId);
      console.log('⚡ Using parallel queries for faster loading...');
      
      // Step 1: Get batches first to know which batch IDs belong to this coaching
      const batchesSnapshot = await getDocs(query(
        collection(db, 'batches'),
        where('coachingId', '==', coachingId)
      ));
      
      const batches = batchesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const batchIds = batches.map(b => b.id);
      
      console.log(`✓ Batches loaded: ${batches.length}`, batchIds);
      
      // Step 2: Load remaining data in PARALLEL
      // Load students from students collection instead of enrollments
      const [coachesResult, studentsResult, enrollmentsResult, paymentsResult, paymentTransactionsResult] = await Promise.allSettled([
        // Coaches
        getDocs(query(
          collection(db, 'users'),
          where('coachingId', '==', coachingId),
          where('role', '==', 'teacher')
        )),
        
        // Students - Load from students collection
        getDocs(query(
          collection(db, 'students'),
          where('coachingId', '==', coachingId)
        )),
        
        // Batch Enrollments - to link students to batches
        getDocs(query(
          collection(db, 'batchEnrollments'),
          where('coachingId', '==', coachingId)
        )),
        
        // Fee Payments
        getDocs(query(
          collection(db, 'feePayments'),
          where('coachingId', '==', coachingId)
        )),
        
        // Payment Transactions
        getDocs(query(
          collection(db, 'paymentTransactions'),
          where('coachingId', '==', coachingId)
        ))
      ]);
      
      // Extract coaches
      const coaches = coachesResult.status === 'fulfilled'
        ? coachesResult.value.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        : [];
      console.log(`✓ Coaches loaded: ${coaches.length}`);
      
      // Extract students from students collection
      const students = studentsResult.status === 'fulfilled'
        ? studentsResult.value.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        : [];
      console.log(`✓ Students loaded: ${students.length}`);
      
      // Extract batch enrollments
      const enrollments = enrollmentsResult.status === 'fulfilled'
        ? enrollmentsResult.value.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        : [];
      console.log(`✓ Batch enrollments loaded: ${enrollments.length}`);
      
      // Extract and combine payments from both collections
      const feePayments = paymentsResult.status === 'fulfilled'
        ? paymentsResult.value.docs.map(doc => ({ id: doc.id, ...doc.data(), source: 'feePayments' }))
        : [];
      
      const paymentTransactions = paymentTransactionsResult.status === 'fulfilled'
        ? paymentTransactionsResult.value.docs.map(doc => ({ id: doc.id, ...doc.data(), source: 'paymentTransactions' }))
        : [];
      
      const payments = [...feePayments, ...paymentTransactions];
      console.log(`✓ Payments loaded: ${payments.length} (${feePayments.length} fees + ${paymentTransactions.length} transactions)`);
      
      const rawData = { batches, coaches, students, enrollments, payments };
      
      console.log('✅ Raw data loaded successfully:', {
        batches: batches.length,
        coaches: coaches.length,
        students: students.length,
        enrollments: enrollments.length,
        payments: payments.length
      });
      
      return rawData;
      
    } catch (error) {
      console.error('❌ Error loading raw data:', error);
      return {
        batches: [],
        coaches: [],
        students: [],
        enrollments: [],
        payments: []
      };
    }
  }, [coachingId]);

  // Step 2: Process raw data into dashboard metrics
  const processData = useCallback((rawData, selectedTimeRange) => {
    if (!rawData) {
      console.error('No raw data provided to processData');
      return;
    }
    
    try {
      const { batches, coaches, students, enrollments, payments } = rawData;
      
      // Validate data arrays
      if (!Array.isArray(batches) || !Array.isArray(coaches) || !Array.isArray(students) || !Array.isArray(enrollments) || !Array.isArray(payments)) {
        console.error('Invalid data structure:', { batches, coaches, students, enrollments, payments });
        throw new Error('Invalid data structure received');
      }
      
      // Calculate date ranges
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(selectedTimeRange));

      // Filter out admin batches (those without coachingId) and process coaching batches only
      const coachingBatches = batches.filter(batch => 
        batch.coachingId && batch.coachingId !== null && batch.coachingId !== ''
      );
      
      console.log('Coaching batches found:', coachingBatches.map(b => ({ 
        id: b.id, 
        name: b.name, 
        status: b.status, 
        coachingId: b.coachingId 
      })));
      
      // Count active coaching batches - consider batch active if status is 'active' or undefined/null
      const activeBatches = coachingBatches.filter(batch => 
        batch.status === 'active' || 
        batch.status === 'Active' || 
        !batch.status || 
        batch.status === '' ||
        batch.status === null
      ).length;

      // Filter out admin coaches (those without coachingId) and process coaching coaches only
      const coachingCoaches = coaches.filter(coach => 
        coach.coachingId && coach.coachingId !== null && coach.coachingId !== ''
      );
      
      // Process coaches
      const activeCoaches = coachingCoaches.filter(coach => coach.status === 'active' || !coach.status).length;

      // Process students and revenue using centralized data
      let totalStudents = students.length; // Simply count all students from students collection
      let totalRevenue = 0;
      const batchStudentCounts = [];
      
      console.log(`✓ Total students from students collection: ${totalStudents}`);
      
      // Populate batchStudentCounts by counting students per batch using enrollments
      coachingBatches.forEach(batch => {
        // Count enrollments for this batch
        const batchEnrollments = enrollments.filter(enrollment => enrollment.batchId === batch.id);
        
        // Get student details for these enrollments
        const batchStudents = batchEnrollments.map(enrollment => {
          const student = students.find(s => s.id === enrollment.studentId);
          return student || { id: enrollment.studentId, name: 'Unknown Student' };
        });
        
        if (batchStudents.length > 0 || batch.status === 'active' || !batch.status) {
          batchStudentCounts.push({
            batchId: batch.id,
            batchName: batch.name || batch.batchName || 'Unnamed Batch',
            studentCount: batchStudents.length,
            batchData: batch,
            enrollments: batchStudents // Store students as enrollments for compatibility
          });
        }
      });
      
      console.log(`✓ Created batch student counts for ${batchStudentCounts.length} batches`);
      
      // Initialize coach revenue tracking (only for coaching coaches)
      const coachRevenueMap = {};
      coachingCoaches.forEach(coach => {
        coachRevenueMap[coach.id] = {
          name: coach.displayName || coach.email || 'Unknown Coach',
          revenue: 0,
          students: 0
        };
      });
      
      // Count students per teacher based on their batches
      coachingBatches.forEach(batch => {
        if (batch.teacherId && coachRevenueMap[batch.teacherId]) {
          // Count enrollments for this batch
          const batchEnrollmentCount = enrollments.filter(e => e.batchId === batch.id).length;
          coachRevenueMap[batch.teacherId].students += batchEnrollmentCount;
        }
      });
      
      // Process each coaching batch for revenue tracking
      console.log('Processing coaching batches for revenue...');
      
      // Filter active coaching batches
      const activeBatchList = coachingBatches.filter(batch => 
        batch.status === 'active' || 
        batch.status === 'Active' || 
        !batch.status || 
        batch.status === '' ||
        batch.status === null
      );
      
      console.log(`Processing ${activeBatchList.length} active coaching batches out of ${coachingBatches.length} total coaching batches`);
      
      console.log(`✓ Total students across all active batches: ${totalStudents}`);
      
      // Process payments and link them to batches using batch ID
      console.log('Processing payments and linking to batches...');
      
      // Filter payments by time range
      const timeFilteredPayments = payments.filter(payment => {
        try {
          const paymentDate = payment.createdAt?.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt);
          return paymentDate >= startDate && paymentDate <= endDate;
        } catch (dateError) {
          console.error('Error processing payment date:', payment.id, dateError);
          return false;
        }
      });
      
      console.log(`Processing ${timeFilteredPayments.length} payments in time range`);
      
      // Create a map of active batches by ID for efficient lookup
      const activeBatchMap = {};
      activeBatchList.forEach(batch => {
        activeBatchMap[batch.id] = batch;
      });
      
      let paymentsProcessed = 0;
      let paymentsLinkedToBatches = 0;
      
      timeFilteredPayments.forEach(payment => {
        try {
          paymentsProcessed++;
          const amount = payment.amount || 0;
          
          // Check if payment has a batch ID and if that batch is active
          if (payment.batchId && activeBatchMap[payment.batchId]) {
            const batch = activeBatchMap[payment.batchId];
            totalRevenue += amount;
            paymentsLinkedToBatches++;
            
            console.log(`  Payment ₹${amount} linked to batch "${batch.name || batch.id}"`);
            
            // Attribute revenue to coach
            if (batch.teacherId && coachRevenueMap[batch.teacherId]) {
              coachRevenueMap[batch.teacherId].revenue += amount;
            } else if (batch.teacherId) {
              // Create coach entry if not exists
              if (!coachRevenueMap[batch.teacherId]) {
                coachRevenueMap[batch.teacherId] = {
                  name: `Teacher ${batch.teacherId.slice(-6)}`,
                  revenue: amount,
                  students: 0
                };
              } else {
                coachRevenueMap[batch.teacherId].revenue += amount;
              }
            }
          } else if (!payment.batchId) {
            // Payment without batch ID - still count towards total revenue
            totalRevenue += amount;
            console.log(`  Payment ₹${amount} without batch ID - added to total`);
          } else {
            console.log(`  Payment ₹${amount} for inactive/unknown batch ${payment.batchId} - skipped`);
          }
        } catch (paymentError) {
          console.error('Error processing payment:', payment.id, paymentError);
        }
      });
      
      console.log(`✓ Processed ${paymentsProcessed} payments, ${paymentsLinkedToBatches} linked to active batches`);
      console.log(`✓ Total revenue: ₹${totalRevenue}`);

      // Convert coach revenue map to array
      const coachRevenueArray = Object.values(coachRevenueMap)
        .filter(coach => coach.revenue > 0 || coach.students > 0)
        .sort((a, b) => b.revenue - a.revenue);
      
      // If no coaches have revenue/students, create entries for all coaches with 0 values
      if (coachRevenueArray.length === 0 && coachingCoaches.length > 0) {
        coachingCoaches.forEach(coach => {
          coachRevenueArray.push({
            name: coach.displayName || coach.email || 'Unknown Coach',
            revenue: 0,
            students: 0
          });
        });
      }





      // Calculate growth rates and other metrics (using centralized data)
      let revenueGrowth = 0;
      let studentGrowth = 0;
      
      try {
        // Calculate previous period data for growth
        const previousStartDate = new Date(startDate);
        previousStartDate.setDate(previousStartDate.getDate() - parseInt(selectedTimeRange));
        
        const previousPayments = payments.filter(payment => {
          try {
            const paymentDate = payment.createdAt?.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt);
            return paymentDate >= previousStartDate && paymentDate < startDate;
          } catch (dateError) {
            console.error('Error processing previous payment date:', payment.id, dateError);
            return false;
          }
        });
        
        const previousRevenue = previousPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
        
        if (previousRevenue > 0) {
          revenueGrowth = ((totalRevenue - previousRevenue) / previousRevenue) * 100;
        }
        
        // Calculate student growth (simplified - compare current vs previous period)
        // Since we don't have historical student data, we'll estimate based on current count
        // In a real scenario, you'd query students created in previous period
        const studentGrowthEstimate = 0; // Placeholder - would need historical data
        studentGrowth = studentGrowthEstimate;
        
      } catch (growthError) {
        console.error('Error calculating growth metrics:', growthError);
      }
      
      // Calculate retention rate (simplified without enrollments)
      // Using students count as proxy
      const retentionRate = 100; // Placeholder - would need enrollment status data
      
      // Calculate average revenue per student
      const averageRevenuePerStudent = totalStudents > 0 ? totalRevenue / totalStudents : 0;
      
      // Sort batch student counts
      const sortedBatchCounts = batchStudentCounts.sort((a, b) => b.studentCount - a.studentCount);
      
      // Debug logging
      console.log('Dashboard Data Processed:', {
        coachingId,
        totalBatchesLoaded: batches.length,
        coachingBatches: coachingBatches.length,
        activeBatches,
        totalCoachesLoaded: coaches.length,
        coachingCoaches: coachingCoaches.length,
        activeCoaches,
        totalStudents,
        totalRevenue,
        revenueGrowth: Math.round(revenueGrowth * 10) / 10,
        studentGrowth: Math.round(studentGrowth * 10) / 10,
        batchStudentCounts: sortedBatchCounts,
        activeBatchList: activeBatchList.map(b => ({ id: b.id, name: b.name, status: b.status, coachingId: b.coachingId }))
      });
      
      // Set all the processed data
      const finalDashboardData = {
        totalRevenue: Math.round(totalRevenue),
        totalStudents,
        activeCoaches,
        activeBatches,
        revenueGrowth: Math.round(revenueGrowth * 10) / 10,
        studentGrowth: Math.round(studentGrowth * 10) / 10,
        averageRevenuePerStudent: Math.round(averageRevenuePerStudent),
        retentionRate: Math.round(Math.min(100, Math.max(0, retentionRate)))
      };
      
      console.log('📊 Final Dashboard Data:', finalDashboardData);
      console.log('📊 Batch Student Counts:', sortedBatchCounts);
      
      setDashboardData(finalDashboardData);
      
      setRevenueByCoach(coachRevenueArray);
      setStudentsByBatch(sortedBatchCounts);
      setTopPerformingBatches(sortedBatchCounts.slice(0, 5));
      
      // Generate monthly trends using centralized data
      const trends = [];
      try {
        for (let i = 5; i >= 0; i--) {
          const monthStart = new Date();
          monthStart.setMonth(monthStart.getMonth() - i);
          monthStart.setDate(1);
          monthStart.setHours(0, 0, 0, 0);
          
          const monthEnd = new Date(monthStart);
          monthEnd.setMonth(monthEnd.getMonth() + 1);
          
          const monthlyPayments = payments.filter(payment => {
            try {
              const paymentDate = payment.createdAt?.toDate ? payment.createdAt.toDate() : new Date(payment.createdAt);
              return paymentDate >= monthStart && paymentDate < monthEnd;
            } catch (dateError) {
              console.error('Error processing monthly payment date:', payment.id, dateError);
              return false;
            }
          });
          
          // For monthly students, we'll use a simplified approach
          // In a real scenario, you'd query students created in this month
          const monthlyStudents = students.filter(student => {
            try {
              const createdDate = student.createdAt?.toDate ? student.createdAt.toDate() : new Date(student.createdAt);
              return createdDate >= monthStart && createdDate < monthEnd;
            } catch (dateError) {
              return false;
            }
          });
          
          const monthlyBatches = activeBatchList.filter(batch => {
            try {
              const batchDate = batch.createdAt?.toDate ? batch.createdAt.toDate() : new Date(batch.createdAt);
              return batchDate >= monthStart && batchDate < monthEnd;
            } catch (dateError) {
              console.error('Error processing monthly batch date:', batch.id, dateError);
              return false;
            }
          });
          
          trends.push({
            month: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            revenue: monthlyPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
            students: monthlyStudents.length,
            batches: monthlyBatches.length
          });
        }
      } catch (trendsError) {
        console.error('Error generating monthly trends:', trendsError);
      }
      
      setMonthlyTrends(trends);
      
      // Show message if no real data is available
      if (totalRevenue === 0 && totalStudents === 0 && batches.length === 0) {
        console.log('No coaching data found. This may be because:');
        console.log('1. No payments have been recorded for this coaching center');
        console.log('2. No students are enrolled in batches');
        console.log('3. The coachingId does not match any existing data');
        console.log('4. This is a test environment with no real data');
        console.log('5. User may not have proper permissions to access the data');
      }
      
    } catch (error) {
      console.error('Error in processData:', error);
      throw error;
    }
  }, []);

  // Main data loading function
  const loadDashboardData = useCallback(async () => {
    if (!coachingId) {
      setError('No coaching ID provided');
      setLoading(false);
      return;
    }
    
    const isRefresh = refreshing;
    if (!isRefresh) setLoading(true);
    setError(null);
    
    try {
      console.log('Starting dashboard data load for coaching ID:', coachingId);
      console.log('User authenticated:', !!window.firebase?.auth?.currentUser);
      console.log('Current user:', window.firebase?.auth?.currentUser?.uid);
      
      // Test basic collection access
      console.log('Testing collection access...');
      try {
        const testBatches = await getDocs(query(collection(db, 'batches'), limit(1)));
        console.log('✓ Batches collection accessible, found', testBatches.size, 'documents');
      } catch (error) {
        console.log('✗ Batches collection access failed:', error.message);
      }
      
      try {
        const testUsers = await getDocs(query(collection(db, 'users'), limit(1)));
        console.log('✓ Users collection accessible, found', testUsers.size, 'documents');
      } catch (error) {
        console.log('✗ Users collection access failed:', error.message);
      }
      
      try {
        const testEnrollments = await getDocs(query(collection(db, 'batchEnrollments'), limit(1)));
        console.log('✓ BatchEnrollments collection accessible, found', testEnrollments.size, 'documents');
      } catch (error) {
        console.log('✗ BatchEnrollments collection access failed:', error.message);
      }
      
      try {
        const testPayments = await getDocs(query(collection(db, 'feePayments'), limit(1)));
        console.log('✓ FeePayments collection accessible, found', testPayments.size, 'documents');
      } catch (error) {
        console.log('✗ FeePayments collection access failed:', error.message);
      }
      
      // Step 1: Load all raw data
      const rawData = await loadRawData();
      if (!rawData) {
        console.log('No raw data loaded, using empty data structure');
        // Use empty data structure instead of failing
        const emptyData = {
          batches: [],
          coaches: [],
          enrollments: [],
          payments: []
        };
        setRawData(emptyData);
        processData(emptyData, selectedTimeRange);
        console.log('Dashboard loaded with empty data');
        return;
      }
      
      console.log('Raw data loaded successfully');
      
      // Step 2: Store raw data
      setRawData(rawData);
      
      // Step 3: Process the data
      console.log('Processing data...');
      processData(rawData, selectedTimeRange);
      console.log('Data processing completed');

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      console.error('Error stack:', error.stack);
      setError(error.message);
      toast.error(`Failed to load dashboard data: ${error.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [coachingId, selectedTimeRange, refreshing, loadRawData, processData]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Load data retention: enabled if coachingCenters OR any coaching_admin user has opted in
  useEffect(() => {
    const loadRetentionStatus = async () => {
      if (!coachingId) return;

      try {
        const centerSnap = await getDoc(doc(db, 'coachingCenters', coachingId));
        if (centerSnap.exists() && centerSnap.data().dataRetentionEnabled === true) {
          setDataRetentionEnabled(true);
          return;
        }

        const adminsSnap = await getDocs(
          query(
            collection(db, 'users'),
            where('coachingId', '==', coachingId),
            where('role', '==', 'coaching_admin')
          )
        );
        const anyAdminOptIn = adminsSnap.docs.some((d) => d.data().dataRetentionEnabled === true);
        setDataRetentionEnabled(anyAdminOptIn);
      } catch (error) {
        console.error('Error loading retention status:', error);
      }
    };

    loadRetentionStatus();
  }, [coachingId]);

  // Reprocess data when time range changes (without reloading raw data)
  useEffect(() => {
    if (rawData.batches.length > 0) {
      processData(rawData, selectedTimeRange);
    }
  }, [selectedTimeRange, rawData, processData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadDashboardData();
  };

  const handleTimeRangeChange = (newRange) => {
    setSelectedTimeRange(newRange);
  };

  const exportData = useCallback(async () => {
    if (!coachingId || exporting) return;

    setExporting(true);
    const toastId = toast.loading('Gathering institute data for export…');

    const safe = async (label, fn) => {
      try {
        const data = await fn();
        return { label, ok: true, data: Array.isArray(data) ? data : [], error: null };
      } catch (err) {
        console.warn(`Export: ${label} failed`, err);
        return { label, ok: false, data: [], error: err?.message || String(err) };
      }
    };

    try {
      const batchesRes = await safe('batches', () => fetchByCoachingId('batches', coachingId));
      const batchIds = (batchesRes.data || []).map((b) => b.id).filter(Boolean);

      const parallel = await Promise.all([
        safe('students', () => fetchByCoachingId('students', coachingId)),
        safe('batchEnrollments', () => fetchByCoachingId('batchEnrollments', coachingId)),
        safe('feePayments', () => fetchByCoachingId('feePayments', coachingId)),
        safe('paymentTransactions', () => fetchByCoachingId('paymentTransactions', coachingId)),
        safe('studentFees', () => fetchByCoachingId('studentFees', coachingId)),
        safe('feeReceipts', () => fetchByCoachingId('feeReceipts', coachingId)),
        safe('reportCards', () => fetchByCoachingId('reportCards', coachingId)),
        safe('certificates', () => fetchByCoachingId('certificates', coachingId)),
        safe('tests', () => fetchByCoachingId('tests', coachingId)),
        safe('resources', () => fetchByCoachingId('resources', coachingId)),
        safe('examData', () => fetchByCoachingId('examData', coachingId)),
        safe('coachingAnalyses', () => fetchByCoachingId('coachingAnalyses', coachingId)),
        safe('liveClasses', () => fetchByCoachingId('liveClasses', coachingId)),
        safe('staffUsers', () => fetchStaffUsers(coachingId)),
        safe('batchAssignments', () => fetchByBatchIdIn('batchAssignments', batchIds))
      ]);

      const zip = new JSZip();
      const root = zip.folder('mahstry_institute_export');

      const addFolderFile = (folderPath, filename, json) => {
        const parts = folderPath.split('/').filter(Boolean);
        let dir = root;
        for (const p of parts) dir = dir.folder(p);
        dir.file(filename, JSON.stringify(json, null, 2), { compression: 'DEFLATE' });
      };

      if (batchesRes.ok) {
        addFolderFile('batches', 'batches.json', batchesRes.data);
      } else {
        addFolderFile('batches', 'batches_error.json', { error: batchesRes.error });
      }

      const folderMap = {
        students: ['students', 'students.json'],
        batchEnrollments: ['enrollments', 'batch_enrollments.json'],
        feePayments: ['payments', 'fee_payments.json'],
        paymentTransactions: ['payments', 'payment_transactions.json'],
        studentFees: ['fees', 'student_fees.json'],
        feeReceipts: ['fees', 'fee_receipts.json'],
        reportCards: ['academics', 'report_cards.json'],
        certificates: ['academics', 'certificates.json'],
        tests: ['academics', 'tests.json'],
        resources: ['academics', 'resources.json'],
        examData: ['academics', 'exam_data.json'],
        coachingAnalyses: ['analyses', 'coaching_analyses.json'],
        liveClasses: ['operations', 'live_classes.json'],
        staffUsers: ['staff', 'users.json'],
        batchAssignments: ['operations', 'batch_assignments.json']
      };

      const manifest = {
        exportedAt: new Date().toISOString(),
        coachingId,
        collections: []
      };

      manifest.collections.push({
        name: 'batches',
        folder: 'batches',
        file: batchesRes.ok ? 'batches.json' : 'batches_error.json',
        documentCount: batchesRes.data?.length || 0,
        ok: batchesRes.ok,
        ...(batchesRes.ok ? {} : { error: batchesRes.error })
      });

      parallel.forEach((res) => {
        const map = folderMap[res.label];
        if (!map) return;
        const [folder, filename] = map;
        if (res.ok) {
          addFolderFile(folder, filename, res.data);
          manifest.collections.push({
            name: res.label,
            folder,
            file: filename,
            documentCount: res.data.length,
            ok: true
          });
        } else {
          addFolderFile(folder, `${filename.replace('.json', '')}_error.json`, { error: res.error });
          manifest.collections.push({
            name: res.label,
            folder,
            file: filename,
            documentCount: 0,
            ok: false,
            error: res.error
          });
        }
      });

      const dashboardSnapshot = {
        exportedAt: manifest.exportedAt,
        coachingId,
        metrics: dashboardData,
        monthlyTrends,
        revenueByCoach,
        studentsByBatch: studentsByBatch.map((b) => ({
          batchId: b.batchId,
          batchName: b.batchName,
          studentCount: b.studentCount
        }))
      };
      addFolderFile('summary', 'dashboard_snapshot.json', dashboardSnapshot);

      root.file(
        'manifest.json',
        JSON.stringify(manifest, null, 2),
        { compression: 'DEFLATE' }
      );

      root.file(
        'README.txt',
        [
          'MahStry — Institute data export',
          '========================',
          '',
          `Coaching ID: ${coachingId}`,
          `Generated (UTC): ${manifest.exportedAt}`,
          '',
          'Folders:',
          '  batches/          — Batch definitions',
          '  students/         — Student records (students collection)',
          '  enrollments/      — Batch enrollments',
          '  payments/         — Fee payments and payment transactions',
          '  fees/             — Student fee plans and receipts',
          '  staff/            — User profiles linked to this coaching (FCM tokens removed)',
          '  academics/        — Report cards, certificates, tests, resources, exam data',
          '  operations/       — Live classes, batch assignments (by batch)',
          '  analyses/         — Saved BI / coaching analyses',
          '  summary/          — Dashboard metrics snapshot at export time',
          '',
          'Timestamps are stored as { "_firestoreType": "Timestamp", "iso": "..." }.',
          '',
          'If a collection failed (permissions or index), see *_error.json in that folder and manifest.json.',
          '',
          'This archive may contain personal data. Store and share it securely and in line with your privacy policy.'
        ].join('\n'),
        { compression: 'DEFLATE' }
      );

      toast.loading('Building ZIP file…', { id: toastId });
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
      const safeDate = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const filename = `mahstry_institute_export_${safeDate}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Export downloaded (${filename})`, { id: toastId });
    } catch (err) {
      console.error('Export failed', err);
      toast.error(err?.message || 'Export failed', { id: toastId });
    } finally {
      setExporting(false);
    }
  }, [coachingId, dashboardData, monthlyTrends, revenueByCoach, studentsByBatch]);

  const toggleDataRetention = async () => {
    if (!coachingId) return;

    setLoadingRetention(true);
    try {
      const newStatus = !dataRetentionEnabled;

      // Canonical flag on coachingCenters (matches Cloud Function cleanup)
      await setDoc(
        doc(db, 'coachingCenters', coachingId),
        {
          dataRetentionEnabled: newStatus,
          dataRetentionUpdatedAt: new Date(),
          coachingId
        },
        { merge: true }
      );

      // Keep all coaching_admin profiles in sync so OR-check stays consistent
      const adminsSnap = await getDocs(
        query(
          collection(db, 'users'),
          where('coachingId', '==', coachingId),
          where('role', '==', 'coaching_admin')
        )
      );
      await Promise.all(
        adminsSnap.docs.map((userDoc) =>
          updateDoc(userDoc.ref, {
            dataRetentionEnabled: newStatus,
            dataRetentionUpdatedAt: new Date()
          }).catch((err) => {
            console.warn('Could not sync retention to user doc', userDoc.id, err?.message);
          })
        )
      );

      const centerAfter = await getDoc(doc(db, 'coachingCenters', coachingId));
      const adminsAfter = await getDocs(
        query(
          collection(db, 'users'),
          where('coachingId', '==', coachingId),
          where('role', '==', 'coaching_admin')
        )
      );
      const effectiveOn =
        (centerAfter.exists() && centerAfter.data().dataRetentionEnabled === true) ||
        adminsAfter.docs.some((d) => d.data().dataRetentionEnabled === true);
      setDataRetentionEnabled(effectiveOn);

      if (effectiveOn !== newStatus) {
        toast.error(
          'Retention could not be applied everywhere (e.g. Firestore rules). The toggle reflects the effective setting.'
        );
      } else if (effectiveOn) {
        toast.success('Data retention enabled! Your data will be preserved after July 1st.');
      } else {
        toast.success(
          'Data retention disabled. Operational data may be removed on the annual cleanup date (Student & Teacher management stays).'
        );
      }
    } catch (error) {
      console.error('Error updating retention status:', error);
      toast.error(`Failed to update data retention setting: ${error.message}`);
    } finally {
      setLoadingRetention(false);
    }
  };

  const getGrowthIcon = (growth) => {
    if (growth > 0) return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (growth < 0) return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  const getGrowthColor = (growth) => {
    if (growth > 0) return 'text-green-600 dark:text-green-400';
    if (growth < 0) return 'text-red-600 dark:text-red-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Failed to load dashboard data
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <BarChart3 className="w-6 h-6 mr-2 text-blue-600" />
            Business Intelligence Dashboard
          </h2>
          <div className="flex items-center space-x-4">
            {/* Data Retention Toggle */}
            <div className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
              <Database className="w-4 h-4 text-blue-600" />
              <button
                onClick={toggleDataRetention}
                disabled={loadingRetention}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  dataRetentionEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                } ${loadingRetention ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    dataRetentionEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Data Retention
              </span>
            </div>
            
            <select
              value={selectedTimeRange}
              onChange={(e) => handleTimeRangeChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 3 months</option>
              <option value="365">Last year</option>
            </select>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button
              type="button"
              onClick={exportData}
              disabled={exporting}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
            </button>
          </div>
        </div>
        
        {/* Data Retention Status Message */}
        <div className={`mt-4 p-4 rounded-lg border ${
          dataRetentionEnabled 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-start space-x-3">
            {dataRetentionEnabled ? (
              <Shield className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${
                dataRetentionEnabled 
                  ? 'text-green-800 dark:text-green-200' 
                  : 'text-yellow-800 dark:text-yellow-200'
              }`}>
                {dataRetentionEnabled 
                  ? '✓ Data Retention Enabled - Your data will be retained after July 1st' 
                  : '⚠ Data Retention Disabled - Your operational data will NOT be retained after July 1st unless you enable retention'}
              </p>
              <p className={`text-xs mt-1 ${
                dataRetentionEnabled 
                  ? 'text-green-700 dark:text-green-300' 
                  : 'text-yellow-700 dark:text-yellow-300'
              }`}>
                {dataRetentionEnabled 
                  ? 'All your data including batches, attendance, assignments, and assessments will be preserved annually.' 
                  : 'After July 1st, operational data (batches, attendance, assignments, assessments) will be automatically deleted. Student management, teacher management, and test management data will be preserved.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State Message */}
      {dashboardData.totalRevenue === 0 && dashboardData.totalStudents === 0 && dashboardData.activeBatches === 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6">
          <div className="flex items-start space-x-3">
            <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1" />
            <div>
              <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                No Data Available
              </h3>
              <p className="text-blue-700 dark:text-blue-300 mb-3">
                The dashboard is working, but no coaching data was found for coaching ID: <strong>{coachingId}</strong>
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                This could be because this is a new coaching center, no students are enrolled, or you may not have access to this coaching center's data.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                ₹{dashboardData.totalRevenue.toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            {getGrowthIcon(dashboardData.revenueGrowth)}
            <span className={`text-sm ml-1 ${getGrowthColor(dashboardData.revenueGrowth)}`}>
              {Math.abs(dashboardData.revenueGrowth)}% from last period
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Students</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData.totalStudents}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-full">
              <Users className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center">
            {getGrowthIcon(dashboardData.studentGrowth)}
            <span className={`text-sm ml-1 ${getGrowthColor(dashboardData.studentGrowth)}`}>
              {Math.abs(dashboardData.studentGrowth)}% from last period
            </span>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active Classes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {dashboardData.activeBatches}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                of {rawData.batches?.length || 0} total
              </p>
            </div>
            <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-full">
              <BookOpen className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue per Student</h3>
            <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            ₹{dashboardData.averageRevenuePerStudent.toLocaleString()}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Average revenue per student
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Retention Rate</h3>
            <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {dashboardData.retentionRate}%
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-3">
            <div 
              className="bg-green-600 h-2 rounded-full" 
              style={{ width: `${dashboardData.retentionRate}%` }}
            ></div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Active Teachers</h3>
            <Award className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
            {dashboardData.activeCoaches}
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            Teaching staff members
          </p>
        </div>
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Teacher Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-yellow-600" />
            Teacher Performance
          </h3>
          {revenueByCoach.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Award className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No teacher data available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {revenueByCoach.slice(0, 5).map((coach, index) => {
                const avgRevenuePerStudent = coach.students > 0 
                  ? Math.round(coach.revenue / coach.students) 
                  : 0;
                const performanceScore = Math.min(100, Math.round((coach.revenue / 100000) * 100));
                
                return (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                          {coach.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {coach.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {coach.students} students
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 dark:text-white">
                          ₹{coach.revenue.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          ₹{avgRevenuePerStudent.toLocaleString()}/student
                        </p>
                      </div>
                    </div>
                    
                    {/* Performance Bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Performance</span>
                        <span>{performanceScore}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full" 
                          style={{ width: `${performanceScore}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Batch Performance Overview */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Target className="w-5 h-5 mr-2 text-green-600" />
            Class Performance Overview
          </h3>
          {studentsByBatch.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No batch data available</p>
              <p className="text-xs mt-2">Check if batches exist for coaching ID: {coachingId}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {studentsByBatch.slice(0, 6).map((batch, index) => (
                <div key={batch.batchId} className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {batch.batchName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Batch ID: {batch.batchId.slice(-8)}...
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {batch.studentCount}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      students
                    </p>
                  </div>
                </div>
              ))}
              {studentsByBatch.length > 6 && (
                <div className="text-center pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    + {studentsByBatch.length - 6} more batches
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-purple-600" />
          Monthly Trends
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Month</th>
                <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Revenue</th>
                <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Students</th>
                <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">Classes</th>
              </tr>
            </thead>
            <tbody>
              {monthlyTrends.map((trend, index) => (
                <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                  <td className="py-3 px-4 text-gray-900 dark:text-white">{trend.month}</td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                    ₹{trend.revenue.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                    {trend.students}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                    {trend.batches}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default BusinessIntelligenceDashboard;